import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, ArrowTabStepper, Button, Card, SearchableSelect, useToast,
  type SearchableSelectOption,
} from '@kss/ui';
import { ArrowLeft, ArrowRight, Plus, Save, Trash2 } from 'lucide-react';
import {
  apiClient,
  productService,
  categoryService,
  brandService,
  unitService,
  type Product,
  type ProductCreate,
} from '@kss/api';
import SectionsEditor, { type PanelSection } from '../../components/SectionsEditor';
import ImagePicker from '../../components/ImagePicker';
import { decodeId } from '../../utils/idCodec';
import { nextCode, provisionalCode } from '../../utils/nextCode';
import { useAuth } from '@kss/auth';

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[88px] w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary outline-none focus:border-primary';

const TAX_RATES = [0, 3, 5, 12, 18, 28];

const loadCategoryOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await categoryService.all();
    return (rows ?? [])
      .map((r) => ({ value: r.categoryId ?? '', label: r.name ?? '' }))
      .filter((o) => o.value);
  } catch { return []; }
};
const loadBrandOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await brandService.all();
    return (rows ?? [])
      .map((r) => ({ value: r.brandId ?? '', label: r.name ?? '' }))
      .filter((o) => o.value);
  } catch { return []; }
};
const loadUnitOptions = async (): Promise<SearchableSelectOption[]> => {
  try {
    const rows = await unitService.all();
    return (rows ?? [])
      .map((r) => ({ value: r.code ?? '', label: `${r.code} — ${r.name}` }))
      .filter((o) => o.value);
  } catch { return []; }
};

interface FormState {
  // basics
  code: string;
  name: string;
  sku: string;
  barcode: string;
  hsnCode: string;
  categoryId: string;
  brand: string;
  unit: string;
  shortDescription: string;
  description: string;
  isActive: boolean;
  // sections
  sections: PanelSection[];
  // images
  imageUrl: string;
  galleryImages: string[];
  // pricing
  mrpPrice: string;
  costPrice: string;
  sellingPrice: string;
  discountType: 'none' | 'flat' | 'percent';
  discountValue: string;
  taxRate: string;
  taxType: 'inclusive' | 'exclusive';
  // SEO
  slug: string;
  metaTitle: string;
  metaDescription: string;
  tags: string;
  // attribute (industry-standard)
  gtin: string;
  mpn: string;
  countryOfOrigin: string;
  minOrderQuantity: string;
  maxOrderQuantity: string;
  weight: string;
  dimensions: string;
}

const empty: FormState = {
  code: '', name: '', sku: '', barcode: '', hsnCode: '', categoryId: '', brand: '', unit: 'PCS',
  shortDescription: '', description: '', isActive: true,
  sections: [],
  imageUrl: '', galleryImages: [],
  mrpPrice: '', costPrice: '', sellingPrice: '',
  discountType: 'none', discountValue: '',
  taxRate: '18', taxType: 'exclusive',
  slug: '', metaTitle: '', metaDescription: '', tags: '',
  gtin: '', mpn: '', countryOfOrigin: 'IN',
  minOrderQuantity: '1', maxOrderQuantity: '',
  weight: '', dimensions: '',
};

const STEPS = [
  { key: 'basics',     label: 'Basics' },
  { key: 'sections',   label: 'Sections' },
  { key: 'images',     label: 'Images' },
  { key: 'pricing',    label: 'Pricing' },
  { key: 'seo',        label: 'SEO & meta' },
  { key: 'review',     label: 'Review' },
];

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
}

function computeFinalPrice(f: FormState): { final: number; base: number; tax: number } {
  const sell = Number(f.sellingPrice) || 0;
  const dv = Number(f.discountValue) || 0;
  const taxRate = Number(f.taxRate) || 0;
  let discounted = sell;
  if (f.discountType === 'flat') discounted = Math.max(0, sell - dv);
  else if (f.discountType === 'percent') discounted = Math.max(0, sell * (1 - dv / 100));
  if (f.taxType === 'inclusive') {
    const base = discounted / (1 + taxRate / 100);
    return { final: discounted, base, tax: discounted - base };
  }
  const tax = discounted * (taxRate / 100);
  return { final: discounted + tax, base: discounted, tax };
}

export default function NewProduct() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const { user } = useAuth();
  const isAdmin = /admin/i.test(String(user?.role ?? ''));

  const [form, setForm] = useState<FormState>(empty);
  const [step, setStep] = useState(0);

  // Auto-fill the next product code (PRD-00004 = max existing + 1) on a
  // NEW product. Read-only for non-admins so codes stay sequential.
  useEffect(() => {
    if (isEdit) return;
    setForm((f) => (f.code ? f : { ...f, code: provisionalCode('PRD') }));
    void (async () => {
      const code = await nextCode('/master-data/Product', 'PRD');
      setForm((f) => (f.code && f.code !== provisionalCode('PRD') ? f : { ...f, code }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);
  const [visitedIndices, setVisitedIndices] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try {
          const r = await productService.get(id);
          if (cancelled) return;
          let sections: PanelSection[] = [];
          try { if (r.sectionsJson) sections = JSON.parse(r.sectionsJson); } catch { /* ignore */ }
          setForm({
            ...empty,
            code: (r as { code?: string }).code ?? '',
            name: r.name ?? '',
            sku: r.sku ?? '',
            barcode: r.barcode ?? '',
            hsnCode: r.hsnCode ?? '',
            categoryId: r.categoryId ?? '',
            brand: r.brand ?? '',
            unit: r.unit ?? 'PCS',
            shortDescription: r.shortDescription ?? '',
            description: r.description ?? '',
            isActive: !!r.isActive,
            sections,
            imageUrl: r.imageUrl ?? '',
            galleryImages: [],
            mrpPrice: r.mrpPrice != null ? String(r.mrpPrice) : '',
            costPrice: r.costPrice != null ? String(r.costPrice) : '',
            sellingPrice: r.sellingPrice != null ? String(r.sellingPrice) : '',
            taxRate: r.taxRate != null ? String(r.taxRate) : '18',
            slug: r.slug ?? '',
            metaTitle: r.metaTitle ?? '',
            metaDescription: r.metaDescription ?? '',
            tags: r.tags ?? '',
            gtin: r.gtin ?? '',
            mpn: r.mpn ?? '',
            countryOfOrigin: r.countryOfOrigin ?? 'IN',
            minOrderQuantity: r.minOrderQuantity != null ? String(r.minOrderQuantity) : '1',
            maxOrderQuantity: r.maxOrderQuantity != null ? String(r.maxOrderQuantity) : '',
            weight: r.weight != null ? String(r.weight) : '',
            dimensions: r.dimensions ?? '',
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setErr(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  // Auto-derive slug from name when empty.
  useEffect(() => {
    if (!form.slug && form.name) set('slug', slugify(form.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name]);

  function validate(): string | null {
    if (!form.code.trim()) return 'Code is required';
    if (!form.name.trim()) return 'Name is required';
    if (!form.sku.trim()) return 'SKU is required';
    if (!form.sellingPrice || Number(form.sellingPrice) <= 0) return 'Sell price must be > 0';
    if (!form.taxRate) return 'Tax rate is required';
    return null;
  }

  async function save() {
    const e = validate();
    if (e) { toast.error('Fix errors', e); return; }
    setSaving(true);
    try {
      // G56: upload any data: URL to /api/Image/upload → hosted URL.
      // Otherwise a base64 string overflows the narrow imageUrl column.
      const { persistImage } = await import('../../utils/persistImage');
      const hostedImageUrl = await persistImage(form.imageUrl);
      // §35: backend renamed several columns; the generated ProductCreate type
      // lags the live swagger, so send both old + new names via a loose cast.
      const payload = {
        code: form.code || form.sku || undefined,   // business code (PRD-00004)
        name: form.name,
        sku: form.sku,
        barcode: form.barcode || undefined,
        hsnCode: form.hsnCode || undefined,
        categoryId: form.categoryId || undefined,
        brand: form.brand || undefined,
        unit: form.unit || undefined,
        unitOfMeasure: form.unit || undefined,       // §35: new schema name
        shortDescription: form.shortDescription || undefined,
        description: form.description || undefined,
        sectionsJson: form.sections.length ? JSON.stringify(form.sections) : undefined,
        imageUrl: hostedImageUrl || undefined,
        mrpPrice: form.mrpPrice ? Number(form.mrpPrice) : undefined,
        mrp: form.mrpPrice ? Number(form.mrpPrice) : undefined,        // §35
        costPrice: form.costPrice ? Number(form.costPrice) : undefined,
        cost: form.costPrice ? Number(form.costPrice) : undefined,     // §35
        sellingPrice: Number(form.sellingPrice),
        price: Number(form.sellingPrice),            // §35: the field POS + invoices read
        currency: 'INR',                             // §35: required on Product.Add
        isService: false,
        isTrackStock: true,
        taxRate: Number(form.taxRate),
        slug: form.slug || undefined,
        metaTitle: form.metaTitle || undefined,
        metaDescription: form.metaDescription || undefined,
        tags: form.tags || undefined,
        gtin: form.gtin || undefined,
        mpn: form.mpn || undefined,
        countryOfOrigin: form.countryOfOrigin || undefined,
        minOrderQuantity: form.minOrderQuantity ? Number(form.minOrderQuantity) : undefined,
        maxOrderQuantity: form.maxOrderQuantity ? Number(form.maxOrderQuantity) : undefined,
        weight: form.weight ? Number(form.weight) : undefined,
        dimensions: form.dimensions || undefined,
        isActive: form.isActive,
      } as ProductCreate & Record<string, unknown>;
      if (isEdit && id) {
        await productService.update([{ productId: id, ...payload } as Product]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        await productService.create([payload]);
        toast.success('Saved', `${form.name} added`);
      }
      // ⑦ Auto-link the entered barcode into the ProductBarcode table so it
      // appears in the Product Barcode section, linked to this product.
      if (form.barcode) await linkBarcode(form.barcode);
      navigate('/panel/products');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally { setSaving(false); }
  }

  // ⑦ Product Barcode helpers ------------------------------------------------
  const pickRows = (d: unknown): Array<Record<string, unknown>> => {
    if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
    const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
    if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
    if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
    if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
    return [];
  };
  async function resolveProductId(): Promise<number | null> {
    try {
      const rows = pickRows((await apiClient.get('/master-data/Product')).data);
      const wantCode = String(form.code || form.sku || '');
      const row = rows.find((x) =>
        (isEdit && id != null && (String(x.id) === String(id) || String(x.code) === String(id))) ||
        (!!wantCode && String(x.code) === wantCode),
      );
      return row ? Number(row.id) : null;
    } catch { return null; }
  }
  /** Upsert a ProductBarcode row linking `value` to the current product. */
  async function linkBarcode(value: string) {
    try {
      const pid = await resolveProductId();
      if (pid == null) return;
      const rows = pickRows((await apiClient.get('/master-data/ProductBarcode')).data);
      if (rows.find((b) => String(b.productId) === String(pid) && String(b.barcode) === String(value))) return;
      await apiClient.post('/master-data/ProductBarcode', {
        code: `BC-${value}`, name: value, status: 0,
        productId: pid, barcode: value, barcodeType: 'CODE128', isPrimary: true,
      });
    } catch { /* non-fatal — the product itself already saved */ }
  }
  /** Generate a valid EAN-13 (890 prefix + timestamp + check digit). */
  function genEan13(): string {
    const base = ('890' + String(Date.now())).slice(0, 12).padEnd(12, '0');
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += Number(base[i]) * (i % 2 === 0 ? 1 : 3);
    return base + String((10 - (sum % 10)) % 10);
  }

  const isLast = step === STEPS.length - 1;
  function goNext() {
    if (step === 0) {
      if (!form.name.trim()) { toast.error('Fix errors', 'Name is required'); return; }
      if (!form.sku.trim()) { toast.error('Fix errors', 'SKU is required'); return; }
    }
    if (step === 3) {
      if (!form.sellingPrice || Number(form.sellingPrice) <= 0) {
        toast.error('Fix errors', 'Sell price must be > 0'); return;
      }
    }
    setSavedIndices((p) => p.includes(step) ? p : [...p, step]);
    setStep(step + 1);
  }
  function goBack() {
    setVisitedIndices((p) => p.includes(step) ? p : [...p, step]);
    setStep(Math.max(0, step - 1));
  }

  const pricing = useMemo(() => computeFinalPrice(form), [form]);

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/panel/products')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-base font-semibold text-content-primary">
            {isEdit ? 'Edit product' : 'New product'}
          </h2>
        </div>
        <div className="mb-3">
          <ArrowTabStepper
            compact
            steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
            activeIndex={step}
            savedIndices={savedIndices}
            visitedIndices={visitedIndices}
            onChange={(i) => setStep(i)}
          />
        </div>

        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <>
            {step === 0 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Code <span className="text-danger">*</span></span>
                  <input
                    className={`${inputCls} ${(!isAdmin || isEdit) ? 'cursor-not-allowed bg-surface-muted text-content-muted' : ''}`}
                    value={form.code}
                    onChange={(e) => set('code', e.target.value.toUpperCase())}
                    readOnly={!isAdmin || isEdit}
                    placeholder="PRD-00004"
                  />
                  <span className="text-[10px] text-content-muted">
                    {isEdit ? 'Code is fixed once created.' : (isAdmin ? 'Auto-numbered (admins may edit).' : 'Auto-numbered.')}
                  </span>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Name <span className="text-danger">*</span></span>
                  <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Punjabi Chole Bhature" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">SKU <span className="text-danger">*</span></span>
                  <input className={inputCls} value={form.sku} onChange={(e) => set('sku', e.target.value.toUpperCase())} placeholder="SKU000001" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Barcode</span>
                  <div className="flex gap-1">
                    <input className={inputCls} value={form.barcode} onChange={(e) => set('barcode', e.target.value)} placeholder="Scan or generate" />
                    <button
                      type="button"
                      onClick={() => set('barcode', genEan13())}
                      className="shrink-0 rounded-md border border-border px-2 text-xs font-medium text-primary hover:bg-surface-card-hover"
                      title="Generate an EAN-13 barcode"
                    >
                      Generate
                    </button>
                  </div>
                  <span className="text-[10px] text-content-muted">Saved into Product Barcodes &amp; linked to this product.</span>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">HSN code</span>
                  <input className={inputCls} value={form.hsnCode} onChange={(e) => set('hsnCode', e.target.value)} placeholder="2106" />
                </label>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Category</span>
                  <SearchableSelect value={form.categoryId} onChange={(v) => set('categoryId', v)} loadOptions={loadCategoryOptions} placeholder="Pick category" clearable />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Brand</span>
                  <SearchableSelect value={form.brand} onChange={(v) => set('brand', v)} loadOptions={loadBrandOptions} placeholder="Pick brand" clearable />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Unit</span>
                  <SearchableSelect value={form.unit} onChange={(v) => set('unit', v)} loadOptions={loadUnitOptions} placeholder="PCS, KG, LTR" />
                </div>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Status</span>
                  <select className={selectCls} value={form.isActive ? 'active' : 'inactive'} onChange={(e) => set('isActive', e.target.value === 'active')}>
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Short description</span>
                  <input className={inputCls} maxLength={200} value={form.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} placeholder="One-line teaser shown on cards & search" />
                </label>
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Description</span>
                  <textarea className={textareaCls} rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} />
                </label>
              </div>
            )}

            {step === 1 && <SectionsEditor value={form.sections} onChange={(v) => set('sections', v)} />}

            {step === 2 && (
              <div className="grid gap-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <ImagePicker label="Primary image" value={form.imageUrl} onChange={(v) => set('imageUrl', v)} hint="Shown as the product thumbnail and in the storefront hero" />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-content-secondary">Gallery images ({form.galleryImages.length})</span>
                    <Button size="sm" variant="outline" onClick={() => set('galleryImages', [...form.galleryImages, ''])}>
                      <Plus className="h-3.5 w-3.5" /> Add image
                    </Button>
                  </div>
                  {form.galleryImages.length === 0 && (
                    <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-content-muted">
                      No gallery images yet.
                    </div>
                  )}
                  {form.galleryImages.map((url, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_auto] items-end gap-2">
                      <ImagePicker
                        label={`Image ${idx + 1}`}
                        value={url}
                        onChange={(v) => {
                          const next = form.galleryImages.slice();
                          next[idx] = v;
                          set('galleryImages', next);
                        }}
                      />
                      <Button size="sm" variant="ghost" onClick={() => set('galleryImages', form.galleryImages.filter((_, i) => i !== idx))} title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  <p className="text-[11px] text-content-muted">Gallery URLs persist locally for now. The backend storage column lands with the §11A images-table migration.</p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid gap-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">MRP</span>
                    <input type="number" className={inputCls} value={form.mrpPrice} onChange={(e) => set('mrpPrice', e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Cost price</span>
                    <input type="number" className={inputCls} value={form.costPrice} onChange={(e) => set('costPrice', e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Sell price <span className="text-danger">*</span></span>
                    <input type="number" className={inputCls} value={form.sellingPrice} onChange={(e) => set('sellingPrice', e.target.value)} />
                  </label>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Discount type</span>
                    <select className={selectCls} value={form.discountType} onChange={(e) => set('discountType', e.target.value as FormState['discountType'])}>
                      <option value="none">None</option>
                      <option value="flat">Flat ₹</option>
                      <option value="percent">Percentage %</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Discount value</span>
                    <input type="number" disabled={form.discountType === 'none'} className={inputCls} value={form.discountValue} onChange={(e) => set('discountValue', e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Tax rate (%) <span className="text-danger">*</span></span>
                    <select className={selectCls} value={form.taxRate} onChange={(e) => set('taxRate', e.target.value)}>
                      {TAX_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </label>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Tax type</span>
                    <select className={selectCls} value={form.taxType} onChange={(e) => set('taxType', e.target.value as FormState['taxType'])}>
                      <option value="exclusive">Exclusive (added on top)</option>
                      <option value="inclusive">Inclusive (already in price)</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Min order qty</span>
                    <input type="number" className={inputCls} value={form.minOrderQuantity} onChange={(e) => set('minOrderQuantity', e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-content-secondary">Max order qty</span>
                    <input type="number" className={inputCls} value={form.maxOrderQuantity} onChange={(e) => set('maxOrderQuantity', e.target.value)} />
                  </label>
                </div>
                <div className="rounded-md border border-border bg-surface-card-hover p-4">
                  <div className="text-xs uppercase tracking-wide text-content-muted">Live calculation</div>
                  <div className="mt-2 grid gap-2 md:grid-cols-3">
                    <PriceTile label="Final customer price" value={pricing.final} highlight />
                    <PriceTile label="Base (pre-tax)" value={pricing.base} />
                    <PriceTile label="Tax component" value={pricing.tax} />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Slug</span>
                  <input className={inputCls} value={form.slug} onChange={(e) => set('slug', slugify(e.target.value))} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Tags (comma-separated)</span>
                  <input className={inputCls} value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="spicy, vegan, bestseller" />
                </label>
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Meta title</span>
                  <input className={inputCls} maxLength={70} value={form.metaTitle} onChange={(e) => set('metaTitle', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Meta description</span>
                  <textarea className={textareaCls} maxLength={160} value={form.metaDescription} onChange={(e) => set('metaDescription', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">GTIN (EAN/UPC)</span>
                  <input className={inputCls} value={form.gtin} onChange={(e) => set('gtin', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">MPN</span>
                  <input className={inputCls} value={form.mpn} onChange={(e) => set('mpn', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Country of origin</span>
                  <input className={inputCls} value={form.countryOfOrigin} onChange={(e) => set('countryOfOrigin', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Weight (kg)</span>
                  <input type="number" className={inputCls} value={form.weight} onChange={(e) => set('weight', e.target.value)} />
                </label>
                <label className="flex flex-col gap-1 md:col-span-2">
                  <span className="text-xs font-medium text-content-secondary">Dimensions</span>
                  <input className={inputCls} value={form.dimensions} onChange={(e) => set('dimensions', e.target.value)} placeholder="10 × 8 × 3 inches" />
                </label>
              </div>
            )}

            {step === 5 && (
              <div className="grid gap-4">
                <div className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-2">
                  <Row k="Name" v={form.name} />
                  <Row k="SKU" v={form.sku} />
                  <Row k="Category" v={form.categoryId || '—'} />
                  <Row k="Brand" v={form.brand || '—'} />
                  <Row k="HSN" v={form.hsnCode || '—'} />
                  <Row k="Unit" v={form.unit} />
                  <Row k="MRP" v={form.mrpPrice ? `₹${form.mrpPrice}` : '—'} />
                  <Row k="Sell price" v={form.sellingPrice ? `₹${form.sellingPrice}` : '—'} />
                  <Row k="Discount" v={form.discountType === 'none' ? '—' : `${form.discountValue} ${form.discountType === 'flat' ? '₹' : '%'}`} />
                  <Row k="Tax" v={`${form.taxRate}% ${form.taxType}`} />
                  <Row k="Final price" v={pricing.final.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })} />
                  <Row k="Sections" v={`${form.sections.length}`} />
                  <Row k="Gallery images" v={`${form.galleryImages.length}`} />
                  <Row k="Slug" v={form.slug || '—'} />
                  <Row k="Tags" v={form.tags || '—'} />
                  <Row k="Status" v={form.isActive ? 'Active' : 'Inactive'} />
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="text-xs text-content-muted">Step {step + 1} of {STEPS.length} — {STEPS[step]?.label}</div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/panel/products')} disabled={saving}>Cancel</Button>
            {step > 0 && <Button variant="outline" onClick={goBack} disabled={saving}><ArrowLeft className="h-4 w-4" /> Back</Button>}
            {!isLast && <Button onClick={goNext} disabled={saving}>Next <ArrowRight className="h-4 w-4" /></Button>}
            {isLast && (
              <Button onClick={save} disabled={saving || loading}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save product'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/50 pb-1.5 last:border-0 last:pb-0">
      <span className="text-xs text-content-muted">{k}</span>
      <span className="text-sm font-medium text-content-primary">{v || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}

function PriceTile({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className={`rounded p-3 ${highlight ? 'bg-primary/5 ring-1 ring-primary/20' : 'bg-surface-card'}`}>
      <div className="text-[11px] uppercase tracking-wide text-content-muted">{label}</div>
      <div className={`mt-1 text-lg font-semibold ${highlight ? 'text-primary' : 'text-content-primary'}`}>
        {value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}
